import React from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Table, { Column, TableActions } from 'components/Table';
import api, { ApiResponses } from 'lib/trpcClient';
import { RoleScope } from 'further-types/admin-user-role';
import ErrorBoundary from 'components/ErrorBoundary';
import PageFilters, { Filter } from 'components/PageFilters';
import SearchField from 'components/SearchField';
import {
  createAdminUserRoleCreateRoute,
  createAdminUserRoleEditRoute,
  createAdminUserRoleViewRoute,
} from 'adminConstants/routes';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import Spacing from 'components/Spacing';
import Row from 'components/Layout/Row';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import BasicSelect from 'further-ui/components/BasicSelect';
import { AdminUserRealm } from 'further-types/admin-user';

const PAGE_ID = 'roles';
type Role = ApiResponses['adminUserRole']['list']['results'][number];

const AdminUserRolesContent: React.FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const deleteDisclosure = useDisclosure<{
    roleId?: string;
    scope?: RoleScope;
  }>();

  const { pagination, filters, handleChangeFilters } = usePaginatedFilters(
    PAGE_ID,
    {
      defaultFilters: {
        searchQuery: '',
        firmId,
        realm: AdminUserRealm.Eis,
      },
      defaultOrder: {
        orderBy: 'name' as const,
        order: 'asc' as const,
      },
      orderByOptions: ['name', 'createdAt', 'updatedAt'],
    },
  );

  const deleteRole = api.adminUserRole.deleteRole.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Role deleted successfully.',
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const [data, query] = api.adminUserRole.list.useSuspenseQuery({
    searchQuery: filters.searchQuery,
    realm: filters.realm,
    ...pagination,
  });
  const roles = data.results;

  const columns: Array<Column<Role>> = [
    {
      label: 'Name',
      key: 'name',
      sort: false,
    },
    {
      label: 'Scope',
      key: 'firmName',
      sort: false,
      render: (row: Role) =>
        row.scope === RoleScope.Firm && row.firmName
          ? isSuperAdmin
            ? `Firm: ${row.firmName}`
            : 'Your firm only'
          : 'System-wide',
    },
    {
      label: 'Description',
      key: 'description',
      sort: false,
    },
    {
      label: '',
      key: 'actions',
      render: (row) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              link: createAdminUserRoleEditRoute(row.id),
              icon: Edit,
              label: 'Edit',
              color: 'primary',
              visible:
                row.realm === AdminUserRealm.Super
                  ? false
                  : row.scope === RoleScope.Global
                  ? isSuperAdmin
                  : true,
            },
            {
              link: createAdminUserRoleViewRoute(row.id),
              icon: Visibility,
              label: 'View',
              color: 'primary',
              visible:
                row.realm === AdminUserRealm.Super
                  ? false
                  : row.scope === RoleScope.Global
                  ? !isSuperAdmin
                  : false,
            },
            {
              icon: Delete,
              onClick: () => {
                deleteDisclosure.stageAction({
                  roleId: row.id,
                  scope: row.scope,
                });
              },
              label: 'Delete',
              color: 'error',
              visible:
                row.realm === AdminUserRealm.Super
                  ? false
                  : row.scope === RoleScope.Global
                  ? isSuperAdmin
                  : true,
            },
          ]}
        />
      ),
      sort: false,
    },
  ];

  return (
    <>
      <PageFilters
        pageId={PAGE_ID}
        buttons={[
          {
            label: `Add ${filters.realm} Role`,
            link: createAdminUserRoleCreateRoute(filters.realm),
            visible: filters.realm !== AdminUserRealm.Super,
          },
        ]}
      >
        <Filter>
          <SearchField
            autoSearchOnDebounce
            name="searchQuery"
            placeholder="Search by name"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
          />
        </Filter>
        {isSuperAdmin ? (
          <Filter>
            <BasicSelect
              value={filters.realm}
              onChange={(realm) => {
                handleChangeFilters({ realm });
              }}
              options={[
                { value: AdminUserRealm.Super, label: 'Super' },
                { value: AdminUserRealm.Eis, label: 'EIS' },
                { value: AdminUserRealm.Lp, label: 'LP' },
              ]}
              placeholder="Select realm"
              fullWidth
            />
          </Filter>
        ) : null}
      </PageFilters>
      <Table
        columns={columns}
        loading={query.isFetching || pagination.isPaginationChanging}
        tablebody={roles}
        emptyMessage="No roles found."
      />
      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={() => {
          deleteRole.mutate({
            roleId: deleteDisclosure.actionProps.roleId ?? '',
          });
        }}
        title="Delete Role"
        content={
          <Spacing size="sm">
            <Typography>
              Are you sure you want to delete this role? Any users that
              currently have this role will lose it.
            </Typography>
            {deleteRole.isPending &&
            deleteDisclosure.actionProps.scope === RoleScope.Global ? (
              <Row centered justify="center" spacing="xs">
                <CircularProgress size={16} />
                <Typography>
                  Checking if this role is in use. This may take some time.
                </Typography>
              </Row>
            ) : null}
          </Spacing>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete',
        }}
        confirmBtnProps={{
          disabled: deleteRole.isPending,
          color: 'secondary',
        }}
        cancelBtnProps={{
          disabled: deleteRole.isPending,
        }}
      />
    </>
  );
};

const AdminUserRolesPage: React.FC = () => {
  return (
    <PageContainer heading="Roles">
      <PageContent withSpacing>
        <ErrorBoundary>
          <AdminUserRolesContent />
        </ErrorBoundary>
      </PageContent>
    </PageContainer>
  );
};

export default AdminUserRolesPage;
